import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, X } from 'lucide-react';
import { LoaderButton } from '../common/LoadingButton';

export type DeleteEntityType = 'WORK_ITEM' | 'PROJECT';

interface EnhancedDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
  entityType: DeleteEntityType;
  entityKey: string; // Task ID (A-001) or Project Key/ID
  workTypeCategory?: string; // For dynamic nomenclature in Work Items
  counts: {
    workItems?: number;
    subtasks?: number;
    comments: number;
    attachments: number;
  };
}

const EnhancedDeleteModal: React.FC<EnhancedDeleteModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isDeleting,
  entityType,
  entityKey,
  workTypeCategory,
  counts
}) => {
  const [confirmKey, setConfirmKey] = useState('');
  const isConfirmed = confirmKey === entityKey;

  // Reset confirm key when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setConfirmKey('');
    }
  }, [isOpen]);

  // Handle Escape key
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  
  const isProject = entityType === 'PROJECT';
  const hasSubtasks = (counts.subtasks ?? 0) > 0;
  const hasComments = (counts.comments ?? 0) > 0;
  const hasAttachments = (counts.attachments ?? 0) > 0;
  const isSingularSubItem = counts.subtasks === 1;
  const subItemLabel = workTypeCategory === 'EPIC'
    ? (isSingularSubItem ? 'Child Item' : 'Child Items')
    : (isSingularSubItem ? 'Subtask' : 'Subtasks');
  const showBreakdown = isProject || hasSubtasks || hasComments || hasAttachments;

  // Build the dependency clause dynamically based on what actually exists
  const dependencyParts: string[] = [];
  if (hasSubtasks) dependencyParts.push(subItemLabel.toLowerCase());
  if (hasComments) dependencyParts.push('comments');
  if (hasAttachments) dependencyParts.push('attachments');
  const dependencyClause =
    dependencyParts.length === 0
      ? null
      : dependencyParts.length === 1
      ? dependencyParts[0]
      : dependencyParts.slice(0, -1).join(', ') + ', and ' + dependencyParts[dependencyParts.length - 1];



  const title = isProject ? 'Delete Project' : 'Delete Work Item';
  const ctaText = isProject ? 'Delete Project' : 'Delete Work Item';

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    // Intentionally do nothing — backdrop clicks don't close or trigger anything
  };

  const handlePanelClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70" />

      {/* Panel */}
      <div
        className="relative w-full max-w-md rounded-2xl bg-primary border border-primary-subtle p-6 text-left shadow-2xl"
        onClick={handlePanelClick}
      >
        {/* Close icon */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          className="absolute top-4 right-4 text-neutral-medium hover:text-white transition-colors p-1 rounded-md hover:bg-white/5"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center shrink-0">
            <AlertCircle className="text-red-500" size={24} />
          </div>
          <h3 className="text-xl font-bold leading-6 text-white">{title}</h3>
        </div>

        <div className="mt-2 text-sm text-neutral-medium space-y-4">
          <div className="text-neutral-light leading-relaxed">
            {isProject ? (
              <p>
                This project has associated data. Deleting this project will permanently remove the project along with all its work items, comments, and attachments.
              </p>
            ) : dependencyClause ? (
              <p>
                Deleting this work item will also permanently remove its associated{' '}
                <span className="text-white font-medium">{dependencyClause}</span>.
              </p>
            ) : (
              <p>
                Are you sure you want to delete this work item? This action is permanent and cannot be reversed.
              </p>
            )}
          </div>

          {showBreakdown && (
            <div className="bg-primary-variant/50 rounded-lg p-4 border border-primary-subtle">
              <h4 className="text-white font-medium mb-2">Items that will be deleted:</h4>
              <ul className="space-y-1 text-neutral-light">
                {isProject ? (
                  <>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                      1 Project
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                       {counts.workItems ?? 0} {counts.workItems === 1 ? 'Work Item' : 'Work Items'}
                    </li>
                  </>
                ) : (
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                    1 Work Item
                  </li>
                )}
                {hasSubtasks && (
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                    {counts.subtasks ?? 0} {subItemLabel}
                  </li>
                )}
                {hasComments && (
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                    {counts.comments} {counts.comments === 1 ? 'Comment' : 'Comments'}
                  </li>
                )}
                {hasAttachments && (
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                    {counts.attachments} {counts.attachments === 1 ? 'Attachment' : 'Attachments'}
                  </li>
                )}
              </ul>
            </div>
          )}

          <p className="font-semibold text-red-500">
            This action cannot be undone.
          </p>

          <div className="pt-2">
            <label className="block text-xs font-medium text-neutral-medium mb-2 uppercase tracking-wider">
              To confirm deletion, please type <span className="text-white">{entityKey}</span> below.
            </label>
            <input
              type="text"
              className="w-full bg-primary-variant border border-primary-subtle rounded-lg px-4 py-2 text-white outline-none focus:border-red-500/50 transition-colors"
              placeholder={`Type ${isProject ? 'Project ID' : 'Work Item ID'} to confirm`}
              value={confirmKey}
              onChange={(e) => setConfirmKey(e.target.value)}
              autoComplete="off"
            />
          </div>
        </div>

        <div className="mt-8 flex flex-col-reverse gap-2">
          <button
            type="button"
            className="w-full rounded-xl px-4 py-3 text-sm font-semibold text-neutral-medium hover:text-white hover:bg-white/5 transition-all outline-none border border-transparent hover:border-primary-subtle text-center"
            onClick={(e) => { e.stopPropagation(); onClose(); }}
          >
            Cancel
          </button>
          <LoaderButton
            className={`!w-full !mt-0 !py-3 rounded-xl font-bold transition-all min-h-[48px] ${
              !isConfirmed ? 'opacity-50 cursor-not-allowed grayscale' : 'hover:shadow-lg hover:shadow-red-500/20 active:scale-[0.98]'
            }`}
            text={ctaText}
            isLoading={isDeleting}
            disabled={!isConfirmed || isDeleting}
            onClick={onConfirm}
          />
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EnhancedDeleteModal;
